import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatErr, API } from "@/lib/api";
import { ArrowLeft, Save, FileText, Trash2, Loader2, History } from "lucide-react";
import { toast } from "sonner";
import PalletEditor, { cleanPallets } from "@/components/tms/PalletEditor";
import StopFields from "@/components/tms/StopFields";
import MarginSummary from "@/components/tms/MarginSummary";

const STATUSES = ["En attente", "En cours", "Livré", "Litige"];
const STATUS_CLS = {
    "En attente": "chip-attente",
    "En cours": "chip-cours",
    "Livré": "chip-livre",
    "Litige": "chip-litige",
};

export default function OtDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [ot, setOt] = useState(null);
    const [carriers, setCarriers] = useState([]);
    const [saving, setSaving] = useState(false);
    const [statusComment, setStatusComment] = useState("");
    const [invoices, setInvoices] = useState([]);

    const load = async (isActive = () => true) => {
        try {
            const [{ data }, { data: cs }, { data: invs }] = await Promise.all([
                api.get(`/ot/${id}`),
                api.get("/carriers"),
                api.get("/invoices"),
            ]);
            if (!isActive()) return;
            setOt(data);
            setCarriers(cs);
            setInvoices(invs.filter((i) => i.ot_id === id));
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        }
    };

    useEffect(() => {
        let active = true;
        load(() => active);
        return () => { active = false; };
        // eslint-disable-next-line
    }, [id]);

    if (!ot) return <div className="p-10 text-slate-400"><Loader2 className="animate-spin inline mr-2" />Chargement…</div>;

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                client_name: ot.client_name, client_contact: ot.client_contact,
                client_order_ref: ot.client_order_ref || "",
                goods_description: ot.goods_description,
                weight_kg: Number(ot.weight_kg) || 0, volume_m3: Number(ot.volume_m3) || 0,
                pallets: cleanPallets(ot.pallets),
                pickup_city: ot.pickup_city, pickup_country: ot.pickup_country,
                delivery_city: ot.delivery_city, delivery_country: ot.delivery_country,
                pickup_address: ot.pickup_address || "", pickup_contact_name: ot.pickup_contact_name || "", pickup_contact_phone: ot.pickup_contact_phone || "",
                delivery_address: ot.delivery_address || "", delivery_contact_name: ot.delivery_contact_name || "", delivery_contact_phone: ot.delivery_contact_phone || "",
                carrier_id: ot.carrier_id || null,
                pickup_date: ot.pickup_date || null,
                delivery_date: ot.delivery_date || null,
                status: ot.status, notes: ot.notes || "",
                price_ht: Number(ot.price_ht) || 0,
                purchase_price_ht: Number(ot.purchase_price_ht) || 0,
            };
            const { data } = await api.put(`/ot/${id}`, payload);
            setOt(data);
            toast.success("OT enregistré");
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const updateStatus = async (newStatus) => {
        try {
            const { data } = await api.patch(`/ot/${id}/status`, { status: newStatus, comment: statusComment });
            setOt(data);
            setStatusComment("");
            toast.success(`Statut : ${newStatus}`);
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        }
    };

    const removeOt = async () => {
        if (!confirm(`Supprimer l'OT ${ot.reference} ? Les factures liées seront aussi supprimées.`)) return;
        try {
            await api.delete(`/ot/${id}`);
            toast.success("OT supprimé");
            navigate("/tms/ot");
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        }
    };

    const createInvoice = async () => {
        try {
            const { data } = await api.post("/invoices", { ot_id: id, lines: [], tva_rate: 20 });
            toast.success(`Facture ${data.number} créée`);
            await load();
            navigate(`/tms/invoices?focus=${data.id}`);
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        }
    };

    return (
        <div className="p-6 lg:p-10 pb-24 lg:pb-10 max-w-6xl mx-auto">
            <Link to="/tms/ot" className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-2">
                <ArrowLeft size={12} /> Retour à la liste
            </Link>
            <header className="mt-3 flex flex-wrap items-start justify-between gap-5">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-orange-400 font-semibold">Ordre de transport</div>
                    <h1 className="font-display text-2xl lg:text-3xl font-bold text-white mt-2 font-mono">{ot.reference}</h1>
                    <div className="mt-2"><span className={`chip ${STATUS_CLS[ot.status]}`}>{ot.status}</span></div>
                </div>
                <div className="flex gap-3 flex-wrap">
                    <button onClick={createInvoice} className="kz-btn-ghost text-sm"><FileText size={14} /> Générer facture</button>
                    <button onClick={removeOt} className="kz-btn-ghost text-sm text-red-300 hover:text-red-200"><Trash2 size={14} /> Supprimer</button>
                    <button onClick={save} disabled={saving} data-testid="ot-save-button" className="kz-btn-primary text-sm">
                        {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Enregistrer
                    </button>
                </div>
            </header>

            <div className="mt-8 grid gap-6 lg:grid-cols-3">
                <div className="lg:col-span-2 space-y-6">
                    <Card title="Client &amp; marchandise">
                        <div className="grid md:grid-cols-2 gap-4">
                            <F label="Client" value={ot.client_name} onChange={(v) => setOt({ ...ot, client_name: v })} />
                            <F label="Contact client" value={ot.client_contact} onChange={(v) => setOt({ ...ot, client_contact: v })} />
                            <F label="Réf. commande client" value={ot.client_order_ref} onChange={(v) => setOt({ ...ot, client_order_ref: v })} testid="ot-detail-order-ref" />
                            <div className="hidden md:block" />
                            <F label="Description marchandise" className="md:col-span-2" value={ot.goods_description} onChange={(v) => setOt({ ...ot, goods_description: v })} />
                            <F label="Poids (kg)" type="number" value={ot.weight_kg || ""} onChange={(v) => setOt({ ...ot, weight_kg: v })} />
                            <F label="Volume (m³)" type="number" value={ot.volume_m3 || ""} onChange={(v) => setOt({ ...ot, volume_m3: v })} />
                            <div className="md:col-span-2">
                                <label className="text-xs text-slate-400 mb-1.5 block">Palettes</label>
                                <PalletEditor pallets={ot.pallets} onChange={(p) => setOt({ ...ot, pallets: p })} />
                            </div>
                        </div>
                    </Card>

                    <Card title="Trajet">
                        <div className="grid md:grid-cols-2 gap-4">
                            <StopFields prefix="pickup" title="Enlèvement" value={ot} onChange={setOt} />
                            <StopFields prefix="delivery" title="Destination" value={ot} onChange={setOt} />
                            <F label="Date enlèvement" type="date" value={ot.pickup_date || ""} onChange={(v) => setOt({ ...ot, pickup_date: v })} />
                            <F label="Date livraison prévue" type="date" value={ot.delivery_date || ""} onChange={(v) => setOt({ ...ot, delivery_date: v })} />
                        </div>
                    </Card>

                    <Card title="Transporteur &amp; prix">
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs text-slate-400 mb-1.5 block">Transporteur</label>
                                <select value={ot.carrier_id || ""} onChange={(e) => setOt({ ...ot, carrier_id: e.target.value })}
                                    className="w-full py-2.5 px-3 rounded-xl bg-[#020617] border border-slate-700 text-sm text-white">
                                    <option value="">— Non assigné —</option>
                                    {carriers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div className="hidden md:block" />
                            <F label="Prix d'achat HT (€) — transporteur" type="number" value={ot.purchase_price_ht || ""} onChange={(v) => setOt({ ...ot, purchase_price_ht: v })} testid="ot-detail-purchase" />
                            <F label="Prix de vente HT (€) — client" type="number" value={ot.price_ht || ""} onChange={(v) => setOt({ ...ot, price_ht: v })} testid="ot-detail-sale" />
                            <div className="md:col-span-2">
                                <label className="text-xs text-slate-400 mb-1.5 block">Notes internes</label>
                                <textarea rows={3} value={ot.notes || ""} onChange={(e) => setOt({ ...ot, notes: e.target.value })}
                                    className="w-full py-2.5 px-3 rounded-xl bg-[#020617] border border-slate-700 text-sm text-white" />
                            </div>
                        </div>
                    </Card>

                    {invoices.length > 0 && (
                        <Card title="Factures liées">
                            <ul className="divide-y divide-slate-800">
                                {invoices.map((inv) => (
                                    <li key={inv.id} className="flex items-center justify-between py-3">
                                        <div>
                                            <div className="font-mono text-slate-200">{inv.number}</div>
                                            <div className="text-xs text-slate-500">{inv.issue_date} · {inv.total_ttc?.toLocaleString("fr-FR")} € TTC</div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className={`chip chip-${inv.status === "Payée" ? "payee" : inv.status === "Relance" ? "relance" : "emise"}`}>{inv.status}</span>
                                            <a href={`${API}/invoices/${inv.id}/pdf?lang=fr`} target="_blank" rel="noreferrer" data-testid={`ot-invoice-pdf-fr-${inv.number}`} className="text-xs text-orange-300 hover:text-orange-200">PDF FR ↗</a>
                                            <a href={`${API}/invoices/${inv.id}/pdf?lang=en`} target="_blank" rel="noreferrer" data-testid={`ot-invoice-pdf-en-${inv.number}`} className="text-xs text-orange-300 hover:text-orange-200 ml-2">EN ↗</a>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </Card>
                    )}
                </div>

                <div className="space-y-6">
                    <Card title="Rentabilité de l'opération">
                        <MarginSummary sale={ot.price_ht} purchase={ot.purchase_price_ht} testid="ot-detail-margin" />
                        <p className="text-[11px] text-slate-500 mt-2">Calcul en direct ; pensez à enregistrer.</p>
                    </Card>
                    <Card title="Mise à jour statut">
                        <div className="grid grid-cols-2 gap-2">
                            {STATUSES.map((s) => (
                                <button
                                    key={s}
                                    data-testid={`ot-status-${s.replace(/\s/g, '-')}`}
                                    onClick={() => updateStatus(s)}
                                    className={`py-2 px-3 rounded-lg text-xs border transition ${
                                        s === ot.status
                                            ? "bg-orange-500 border-orange-500 text-[#0b1329] font-semibold"
                                            : "bg-slate-900 border-slate-700 text-slate-200 hover:border-orange-400 hover:text-white"
                                    }`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                        <div className="mt-3">
                            <label className="text-xs text-slate-400 mb-1.5 block">Commentaire (optionnel)</label>
                            <textarea
                                rows={2}
                                value={statusComment}
                                onChange={(e) => setStatusComment(e.target.value)}
                                placeholder="Ex. Enlèvement effectué à 09:40"
                                className="w-full py-2 px-3 rounded-xl bg-[#020617] border border-slate-700 text-sm text-white"
                            />
                        </div>
                    </Card>

                    <Card title="Historique" icon={<History size={14} className="text-orange-400" />}>
                        <ol className="relative border-l border-slate-700 ml-2 space-y-4">
                            {[...(ot.status_history || [])].reverse().map((h, idx) => (
                                <li key={idx} className="ml-4">
                                    <span className="absolute -left-1.5 w-3 h-3 rounded-full bg-orange-400 mt-1" />
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className={`chip ${STATUS_CLS[h.status]} text-[10px]`}>{h.status}</span>
                                        <span className="text-[11px] text-slate-500 font-mono">{new Date(h.at).toLocaleString("fr-FR")}</span>
                                    </div>
                                    <div className="text-xs text-slate-400 mt-1">
                                        {h.by && <span className="text-slate-500">par {h.by}. </span>}
                                        {h.comment}
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </Card>
                </div>
            </div>
        </div>
    );
}

function Card({ title, children, icon }) {
    return (
        <section className="rounded-2xl border border-slate-800 bg-[#0f172a]">
            <div className="px-5 py-3.5 border-b border-slate-800 text-xs uppercase tracking-widest text-slate-400 font-semibold flex items-center gap-2">
                {icon} <span dangerouslySetInnerHTML={{__html: title}} />
            </div>
            <div className="p-5">{children}</div>
        </section>
    );
}
function F({ label, value, onChange, type = "text", className = "", testid }) {
    return (
        <div className={className}>
            <label className="text-xs text-slate-400 mb-1.5 block">{label}</label>
            <input type={type} step={type === "number" ? "any" : undefined} data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
                className="w-full py-2.5 px-3 rounded-xl bg-[#020617] border border-slate-700 text-sm text-white"
            />
        </div>
    );
}
